import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { EventListenerManager } from "abweb/utils/EventRegistration";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { reaction } from "mobx";
export class IdleMonitor {
  constructor() {
    // HTML element to whose events we attach our mouse and keyboard event listeners. If this
    // property is null, this means that we are not attached to any element yet.
    _defineProperty(this, "target", null);
    // Map of timer handles to Timer objects
    _defineProperty(this, "listeners", new Map());
    _defineProperty(this, "eventListeners", new EventListenerManager());
    // Map of timer handles to RPC Service listener disposers
    _defineProperty(this, "rpcServices", new Map());
  }
  /**
   * Adds a listener for the two events - onIdle and onAwake - for the given timeout
   * @param {number} timeout
   * @param {() => void} onIdle
   * @param {() => void} onAwake
   * @param {rpcServiceToObserve} The service whose outstanding request counter should be used to monitor RPCs as an additional
   *                                indicator of user status. Optional - if not provided, RPCs are ignored.
   * @returns an opaque handle value that can be used to remove the listener when it is no longer
   * needed
   */
  static addListener(timeout, onIdle, onAwake, rpcServiceToObserve = null) {
    if (!IdleMonitor.singleton) {
      IdleMonitor.singleton = new IdleMonitor();
      IdleMonitor.singleton.attach(document.body);
    }
    let handle = IdleMonitor.nextTimerHandle++;

    // If an RPC service was supplied, pay attention to it
    if (rpcServiceToObserve) {
      // Start observing the service and save off the disposer that can be used to stop paying attention
      const disposer = IdleMonitor.singleton.attachToRpcService(rpcServiceToObserve);
      IdleMonitor.singleton.rpcServices.set(handle, disposer);
    }
    IdleMonitor.singleton.listeners.set(handle, new Timer(timeout, onIdle, onAwake));
    return handle;
  }

  /**
   * Removes the listener using the handle returned from the addListener method
   * @param {number} handle
   */
  static removeListener(handle) {
    if (handle <= 0 || !IdleMonitor.singleton) {
      return;
    }
    let timer = IdleMonitor.singleton.listeners.get(handle);
    if (!timer) {
      return;
    }

    // let the Timer object to kill the scheduled timeout if any
    timer.destroy();

    // Get the disposed that can be used to stop listening to the RPC service, if any
    const disposer = IdleMonitor.singleton.rpcServices.get(handle);
    if (disposer) {
      // If there is a disposer, use it
      IdleMonitor.singleton.detachFromRpcService(disposer);
      IdleMonitor.singleton.rpcServices.delete(handle);
    }

    // remove the timer from our internal map; if it was the last timer object, detach from the
    // events and destroy the singleton instance.
    IdleMonitor.singleton.listeners.delete(handle);
    if (IdleMonitor.singleton.listeners.size === 0) {
      IdleMonitor.singleton.detach();
      IdleMonitor.singleton = null;
    }
  }
  onUserGesture() {
    // notify all our Timer objects
    this.listeners.forEach(timer => timer.tickle());
  }

  /**
   * Temporarily suspend all timers. This leaves them in place, but stops them from running. It is used when there are outstanding
   * RPCs in the RPC service being monitored
   */
  suspendTimers() {
    this.listeners.forEach(timer => timer.destroy());
  }

  /**
   * Resume all timers. When they have been suspended because there were outstanding RPCs in the RPC service being monitored, this
   * restarts them.
   */
  restartTimers() {
    this.listeners.forEach(timer => timer.start());
  }

  /**
   * Attaches to the events of the given element (or BODY if the target parameter is undefined.
   *
   * Orginally in BaseKMController, but that was moved to Gelato because it generated AbEvents.
   * @param target -- Optional html element to add keyboard and mouse listeners to.  If not there,
   * uses top-level BODY element.
   */
  attach(target) {
    this.detach();
    this.target = target == null ? $(document.body).get(0) : target;
    const userGestureHandler = this.onUserGesture.bind(this);
    this.eventListeners.registerEventListener(this.target, "keydown", userGestureHandler);
    // this.eventListeners.registerEventListener(this.target, "mousemove", userGestureHandler);
    this.eventListeners.registerEventListener(this.target, "keyup", userGestureHandler);
    this.eventListeners.registerEventListener(this.target, "mousedown", userGestureHandler);
    this.eventListeners.registerEventListener(this.target, "mouseup", userGestureHandler);
    this.eventListeners.registerEventListener(this.target, "wheel", userGestureHandler);
    // this.eventListeners.registerEventListener(this.target, "mouseleave", userGestureHandler);
  }

  /**
   * Detaches from the events of the target element and "forgets" the target element.
   */
  detach() {
    if (this.target) {
      this.eventListeners.unregister();
      this.target = null;
    }
  }

  /**
   * Starts observing the RPC service
   *
   * @param service   the service to observe
   * @return {IReactionDisposer}  the disposer that can be used to stop observing the service
   */
  attachToRpcService(service) {
    // When the service's outstanding request count changes between non-zero and zero,
    // this reaction will either suspend or restart the timers
    return reaction(() => service.outstandingRequestCounter.isBusy, busy => {
      if (busy) {
        this.suspendTimers();
      } else {
        this.restartTimers();
      }
    }, {
      fireImmediately: false
    });
  }

  /**
   * Stops observing the RPC Service
   *
   * @param disposer  the disposer used to detach from the service
   */
  detachFromRpcService(disposer) {
    disposer();
  }
}

/**
 * Keeps information about a listener for onIdle and onAwake events.
 */
_defineProperty(IdleMonitor, "LOG2", LogUtils.getLogger(__filename));
// Singleton instance of the class.
_defineProperty(IdleMonitor, "singleton", null);
// Handle value to identify the next Timer object. Handles are returned from the addListener
// method and are passed to the removeListener method.
_defineProperty(IdleMonitor, "nextTimerHandle", 1);
class Timer {
  constructor(delay, onIdle, onAwake) {
    _defineProperty(this, "delay", void 0);
    _defineProperty(this, "onIdle", void 0);
    _defineProperty(this, "onAwake", void 0);
    _defineProperty(this, "lastActivity", 0);
    _defineProperty(this, "timedOut", false);
    _defineProperty(this, "timeoutHandle", 0);
    _defineProperty(this, "onTimeout", () => {
      this.timeoutHandle = 0;
      let idleTime = Date.now() - this.lastActivity;
      if (idleTime > this.delay) {
        this.timedOut = true;
        this.onIdle();
      } else {
        this.timeoutHandle = setTimeout(this.onTimeout, this.delay - idleTime);
      }
    });
    this.delay = delay;
    this.onIdle = onIdle;
    this.onAwake = onAwake;
    this.lastActivity = Date.now();
    this.start();
  }
  start() {
    if (!this.timeoutHandle) {
      this.timeoutHandle = setTimeout(this.onTimeout, this.delay);
    }
  }

  /**
   * Called when user activity is detected
   */
  tickle() {
    if (this.timedOut) {
      this.timeoutHandle = setTimeout(this.onTimeout, this.delay);
      this.timedOut = false;
      this.onAwake();
    }
    this.lastActivity = Date.now();
  }

  /**
   * Called when the listener, for which this Timer object was created, is removed or temporarily suspended
   */
  destroy() {
    if (this.timeoutHandle > 0) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = 0;
    }
  }
}
//# sourceMappingURL=IdleMonitor.js.map
